import axios from "axios";
import { sha256 } from "js-sha256";
import val from "../getThemeDetails";
import { regenerateSecKey } from "../redux/actions/regenerateSecKeyAction";
import { getRegenerateToken } from "../redux/actions/regenerateTokenAction";
import { store } from "../redux/store";
// import { logoutUser } from "../redux/actions/authActions";
import { LOGOUT_SUCCESS } from "../redux/types";

axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.common["Content-Type"] = "application/json";

const vouchers = axios.create({
  baseURL: `${val.host}/voucherServices/REST/v1/`,
});

const instance = axios.create({
  baseURL: `${val.host}/walletServices/`,
});

const account = axios.create({
  baseURL: `${val.host}/accountServices/AccountService/`,
});

const marketPlace = axios.create({
  baseURL: `${val.host}/marketplaceServices/`,
});

const settlement = axios.create({
  baseURL: `${val.host}/settlementServices/REST/v1/`,
});

const accountServices = axios.create({
  baseURL: `${val.host}/accountServices/AccountService/`,
});

const getFeee = axios.create({
  baseURL: `${val.host}/feeServices/FeeServices/`,
});

const kycServices = axios.create({
  baseURL: `${val.host}/kycServices/KYCServices/`,
});

const prepaidServices = axios.create({
  baseURL: `${val.host}/prepaidServices/PrepaidService/`,
});

const partnerServices = axios.create({
  baseURL: `${val.host}/partnerServices/PartnerServices/`,
});

const adminServices = axios.create({
  baseURL: `${val.host}/partnerServices/AdminServices/`,
});

const invoiceServices = axios.create({
  baseURL: `${val.host}/invoiceServices/REST/v1/`,
});

const dealerServices = axios.create({
  baseURL: `${val.host}/partnerServices/DealerServices/`,
});

const bankServices = axios.create({
  baseURL: `${val.host}/partnerServices/BankServices/`,
});

let refreshTokenPromise = null;

const serviceClients = [
  vouchers,
  settlement,
  accountServices,
  instance,
  account,
  marketPlace,
  getFeee,
  kycServices,
  prepaidServices,
  partnerServices,
  adminServices,
  invoiceServices,
  dealerServices,
  bankServices,
];

const getResponseData = (data) => {
  if (!data || typeof data !== "object") return {};

  if (data.responseCode || data.description) return data;

  return (
    Object.values(data).find(
      (value) => value && typeof value === "object" && (value.responseCode || value.description),
    ) || {}
  );
};

const isAuthFailure = (data) => {
  const description = data?.description;
  const responseCode = String(data?.responseCode || "");

  return (
    description === "Authorization failed" ||
    description === "Token generation forbidden" ||
    responseCode.includes("403")
  );
};

const setAuthToken = (config, token) => {
  if (!config.headers) config.headers = {};

  if (typeof config.headers.set === "function") {
    config.headers.set("AuthToken", token);
    config.headers.set("Authorization", token);
    return;
  }

  config.headers.AuthToken = token;
  config.headers.Authorization = token;
};

serviceClients.forEach((fn) => {
  fn.interceptors.request.use(
    (config) => {
      return Promise.resolve(config);
    },
    (error) => Promise.reject(error),
  );

  fn.interceptors.response.use(
    (response) => {
      // :white_check_mark: :fire: FIX: skip blob responses
      if (response.config?.responseType === "blob") {
        return response;
      }
      if (!response.data) return response;

      // :white_check_mark: SAFE extraction
      const responseData = getResponseData(response.data);

      // // :white_check_mark: LOGOUT
      // if (responseData?.responseCode === "42") {
      //   store.dispatch({ type: LOGOUT_SUCCESS });
      // }

      if (
        responseData.responseCode === "42" ||
        response.data.responseCode === "42"
      ) {
        store.dispatch({ type: LOGOUT_SUCCESS });
      }

      if (
        responseData.responseCode === "3" ||
        response.data.responseCode === "3"
      ) {
        const { secKey, customerId } = store.getState().login.data;

        const req = {
          customerId,
          walletOwnerId: val.walletOwnerId,
          token: val.token,
          verificationHash: sha256(secKey + customerId + val.walletOwnerId),
        };
        store.dispatch(regenerateSecKey(req));
      }

      return Promise.resolve(response);
    },
    async (error) => {
      const originalRequest = error.config;
      const responseData = getResponseData(error?.response?.data);

      if (!originalRequest) {
        return Promise.reject(error);
      }

      // :white_check_mark: :fire: IMPORTANT: skip retry for blob
      if (originalRequest?.responseType === "blob" || originalRequest?.skipAuthRefresh) {
        return Promise.reject(error);
      }

      if (isAuthFailure(responseData) && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          if (!refreshTokenPromise) {
            const state = store.getState();
            const { AuthToken, partnerId } = state.login.data;

            const regenerateTokenReq = {
              authentication: { partnerId },
              authToken: sessionStorage.getItem("authToken") || AuthToken,
            };

            refreshTokenPromise = store
              .dispatch(getRegenerateToken(regenerateTokenReq, { skipAuthRefresh: true }))
              .finally(() => {
                refreshTokenPromise = null;
              });
          }

          await refreshTokenPromise;

          const newToken = store.getState().regenerateToken?.data?.AuthToken;

          if (newToken) {
            // :white_check_mark: update global header
            axios.defaults.headers.common["AuthToken"] = newToken;
            axios.defaults.headers.common["Authorization"] = newToken;

            // :white_check_mark: update request header
            serviceClients.forEach((client) => {
              client.defaults.headers.common["AuthToken"] = newToken;
              client.defaults.headers.common["Authorization"] = newToken;
            });

            setAuthToken(originalRequest, newToken);

            // :repeat: retry API
            return fn(originalRequest);
          }

          store.dispatch({ type: LOGOUT_SUCCESS });
        } catch (err) {
          console.error("Token refresh failed", err);
          store.dispatch({ type: LOGOUT_SUCCESS });
        }
      }

      return Promise.reject(error);
    },
    (error) => Promise.reject(error),
  );
});

export default instance;
export {
  account,
  marketPlace,
  vouchers,
  settlement,
  accountServices,
  getFeee,
  kycServices,
  prepaidServices,
  partnerServices,
  adminServices,
  invoiceServices,
  dealerServices,
  bankServices,
};